import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { cn } from '../../utils/cn';

interface AudioPlayerProps {
  src: string;
  className?: string;
  onPlay?: () => void;
  onError?: () => void;
  /** Fired once the recording's length is known (seconds). */
  onDuration?: (seconds: number) => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, className, onPlay, onError, onDuration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(true);
    setLoadError(false);
    audio.load();

    const handleLoadedMetadata = () => {
      const d = audio.duration;
      if (Number.isFinite(d) && d > 0) {
        setDuration(d);
        setLoadError(false);
        onDuration?.(d);
      }
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      setIsLoading(false);
      setLoadError(true);
      setIsPlaying(false);
      onError?.();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [src, onError]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || loadError) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    try {
      if (onPlay) onPlay();
      await audio.play();
      setIsPlaying(true);
    } catch {
      setLoadError(true);
      onError?.();
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number) => {
    if (!Number.isFinite(time) || time < 0) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const maxDuration = duration > 0 ? duration : 0;

  return (
    <div className={cn('flex flex-col space-y-2 w-full', className)}>
      <audio ref={audioRef} src={src} preload="metadata" />

      <div className="flex items-center space-x-2">
        <button
          onClick={() => void togglePlay()}
          disabled={isLoading || loadError}
          className="p-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>

        <div className="flex-1 flex items-center space-x-2">
          <span className="text-xs text-muted-foreground w-8">{formatTime(currentTime)}</span>
          <input
            type="range"
            min="0"
            max={maxDuration}
            value={currentTime}
            onChange={handleSeek}
            disabled={loadError || maxDuration === 0}
            className="flex-1 h-1.5 bg-muted rounded-full appearance-none cursor-pointer disabled:opacity-50"
            style={{
              backgroundSize: maxDuration > 0 ? `${(currentTime / maxDuration) * 100}% 100%` : '0% 100%',
              backgroundImage: 'linear-gradient(to right, var(--primary), var(--primary))',
            }}
          />
          <span className="text-xs text-muted-foreground w-8">
            {isLoading ? '...' : formatTime(duration)}
          </span>
        </div>

        <button
          onClick={toggleMute}
          disabled={loadError}
          className="p-2 rounded-full hover:bg-muted disabled:opacity-50"
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <VolumeX className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Volume2 className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </div>
    </div>
  );
};

export default AudioPlayer;
